/**
 * CodeBlock — блок кода вопроса с подсветкой синтаксиса (Фаза 16, B3).
 *
 * Инвариант безопасности «нет нового HTML-канала» (прецедент —
 * [FormulaText.tsx](./FormulaText.tsx:21)): React рендерит ИСХОДНЫЙ ТЕКСТ
 * внутри `<pre><code>` (авто-экранирование, никакого dangerouslySetInnerHTML);
 * после монтирования hljs.highlightElement читает textContent и сам строит
 * разметку токенов на DOM-узле. Грамматики — лениво через
 * [ensureLanguages](../languages.ts) (гранулярные чанки B2).
 *
 * Фолбэк plaintext: незнакомый язык, незагрузившийся чанк, ошибка подсветки —
 * блок остаётся чистым `<pre><code>` с исходным текстом, квиз работает.
 *
 * Переподсветка при смене code/lang: `<code>` пересоздаётся по key (hljs
 * мутирует innerHTML — React-овский text-node после этого отвязан от DOM,
 * обновить его на месте нельзя); эффект заново подсвечивает свежий элемент.
 *
 * Кнопка «копировать» — паттерн copyText
 * ([EditorJsonPanel.tsx](./quiz/EditorJsonPanel.tsx:120)) с состоянием
 * «скопировано» (сброс через 1.5 с); i18n ×3 (question.code.copy/copied).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CODE_LANG_PLAINTEXT, ensureLanguages, isKnownCodeLang } from '../languages';

export interface CodeBlockProps {
  /** Исходный текст блока кода (рендерится как текст React-ом). */
  code: string;
  /** Код языка из whitelist (plaintext/пусто/незнакомый — без подсветки). */
  lang?: string;
}

/** Копирует текст в буфер: async Clipboard API, fallback — execCommand
 *  (паттерн EditorJsonPanel.copyText — общий для копирующих панелей). */
async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* clipboard занят/недоступен (нет фокуса, HTTP) — пробуем fallback ниже */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

/** Сколько миллисекунд показываем «Скопировано» после успешного копирования. */
const COPIED_RESET_MS = 1500;

export function CodeBlock({ code, lang }: CodeBlockProps): JSX.Element {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const codeRef = useRef<HTMLElement>(null);

  // Подсветим только знакомый не-plaintext язык; всё остальное — plaintext.
  const highlightLang =
    lang && lang !== CODE_LANG_PLAINTEXT && isKnownCodeLang(lang) ? lang : '';

  // Подсветка после монтирования/смены code/lang (ref уже на свежем <code> —
  // key ниже пересоздаёт элемент). Ошибки глотаем: plaintext-фолбэк.
  useEffect(() => {
    const el = codeRef.current;
    if (!el || !highlightLang) return;
    let cancelled = false;
    void ensureLanguages([highlightLang]).then((hljs) => {
      // Гвард getLanguage: чанк мог не загрузиться (ensureLanguages тихо
      // пропустил язык) — тогда остаёмся plaintext без warning-подсветки.
      if (cancelled || !hljs || !hljs.getLanguage(highlightLang)) return;
      try {
        hljs.highlightElement(el);
      } catch {
        /* тишина: блок уже читается как plaintext */
      }
    });
    return () => {
      cancelled = true;
    };
  }, [code, highlightLang]);

  async function handleCopy(): Promise<void> {
    const ok = await copyText(code);
    setCopied(ok);
    if (ok) {
      window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
    }
  }

  return (
    <div className="code-block" data-testid="code-block">
      <button
        type="button"
        className="btn btn--ghost code-block__copy"
        onClick={() => void handleCopy()}
        data-testid="code-copy"
      >
        {copied ? t('question.code.copied') : t('question.code.copy')}
      </button>
      <pre className="code-block__pre">
        {/* key: пересоздание при смене code/lang — hljs мутирует innerHTML,
            React должен рендерить текст в нетронутый элемент (см. шапку). */}
        <code
          key={`${highlightLang}\u0000${code}`}
          className={highlightLang ? `language-${highlightLang}` : undefined}
          ref={codeRef}
        >
          {code}
        </code>
      </pre>
    </div>
  );
}
